import React, { useMemo } from 'react';
import { Row, Col, Form, Button, Table } from 'react-bootstrap';
import { Controller } from 'react-hook-form';
import Select from 'react-select';
import { FaEdit, FaCalendarAlt } from 'react-icons/fa';
import { isValid, isWithinInterval, format as formatFns } from 'date-fns';
import FormInput from '../form/FormInput';
import FormDatePicker from '../form/FormDatePicker';
import ScheduleCalendar from './ScheduleCalendar';
import { STATIC_OPTIONS } from '../../utils/roleConstants';
import { customStyles } from '../../utils/projectSelectUtils';
import { calculateLiveAuditionSeats, calculateOnlineAuditionSeats, getNestedError, countWorkingDays } from '../../utils/roleHelpers';
import { addDays, parse } from 'date-fns';

const StepSchedule = ({
    control, errors, lookupOptions, locationOptions, watchedAuditionType, schedulesData,
    isSchedulingEdit, handleUpdateScheduleClick, handleDoneEditing, selectedScheduleType,
    isCurrentVideo, watchedScheduleForm, liveAuditionCalculations, onlineAuditionCalculations,
    minProjectDate, maxProjectDate,
    showCalendar, setShowCalendar, calDate, setCalDate, dateSpecificOffSlots, toggleSlotAvailability
}) => {
    
    const singleAuditionOption = useMemo(() => selectedScheduleType ? [selectedScheduleType] : [], [selectedScheduleType]);

    // Destructure relevant schedule form values for local use
    const [
        watchedStartDate, watchedEndDate, watchedStartTime, watchedEndTime, watchedCastingTime, 
        watchedWeekDaysOff, , , watchedOnlineSeatsPerDay
    ] = watchedScheduleForm;

    const scheduleRows = useMemo(() => {
        if (!watchedAuditionType) return [];
        return watchedAuditionType.map(opt => {
            const savedData = schedulesData[opt.value];
            
            // Determine if this is the schedule currently being edited
            const isThisScheduleBeingEdited = isSchedulingEdit && selectedScheduleType?.value === opt.value;

            // --- Start Date ---
            let displayStartDate;
            if (isThisScheduleBeingEdited && watchedStartDate && isValid(watchedStartDate)) {
                displayStartDate = formatFns(watchedStartDate, 'dd/MM/yyyy');
            } else if (savedData?.startDate && isValid(new Date(savedData.startDate))) {
                displayStartDate = formatFns(new Date(savedData.startDate), 'dd/MM/yyyy');
            } else {
                displayStartDate = 'N/A';
            }

            // --- End Date ---
            let displayEndDate;
            if (isThisScheduleBeingEdited && watchedEndDate && isValid(watchedEndDate)) {
                displayEndDate = formatFns(watchedEndDate, 'dd/MM/yyyy');
            } else if (savedData?.endDate && isValid(new Date(savedData.endDate))) {
                displayEndDate = formatFns(new Date(savedData.endDate), 'dd/MM/yyyy');
            } else {
                displayEndDate = 'N/A';
            }

            // --- Location ---
            let displayLocation = 'TBD';
            if (opt.label.toLowerCase().includes('online')) {
                displayLocation = 'Web Upload';
            } else if (isThisScheduleBeingEdited && watchedScheduleForm[6]) { // assuming casting_location is at index 6 in watchedScheduleForm, or pass explicitly
                // If casting_location is a Select option, get its label
                displayLocation = watchedScheduleForm[6].label || watchedScheduleForm[6].value || 'TBD';
            } else if (savedData?.castingLocation) {
                displayLocation = savedData.castingLocation.label || savedData.castingLocation.value || savedData.castingLocation || 'TBD';
            }

            // --- Slots per day and total seats/slots ---
            let displaySlotsPerDay = 'N/A';
            let displayTotalSlots = 'N/A';

            if (opt.label.toLowerCase().includes('online')) {
                // If currently editing, use live calculations
                if (isThisScheduleBeingEdited) {
                    displaySlotsPerDay = watchedOnlineSeatsPerDay || 0;
                    displayTotalSlots = onlineAuditionCalculations.total || 0;
                } else { // Otherwise use saved data
                    displaySlotsPerDay = savedData?.numberOfSeatsPerDay || 0;
                    displayTotalSlots = savedData?.numberOfSeats || 0;
                }
            } else { // Live Auditions
                // If currently editing, use live calculations
                if (isThisScheduleBeingEdited) {
                    displaySlotsPerDay = liveAuditionCalculations.perDay || 0;
                    displayTotalSlots = liveAuditionCalculations.total || 0;
                } else { // Otherwise use saved data
                    displaySlotsPerDay = savedData?.numberOfSeatsPerDay || 0;
                    displayTotalSlots = savedData?.numberOfSeats || 0;
                }
            }

            return { 
                type: opt.label, 
                typeValue: opt.value, 
                location: displayLocation,
                startDate: displayStartDate,
                endDate: displayEndDate,
                slotsPerDay: displaySlotsPerDay,
                totalSlots: displayTotalSlots
            };
        });
    }, [
        watchedAuditionType, 
        schedulesData, 
        isSchedulingEdit, 
        selectedScheduleType, 
        watchedStartDate, 
        watchedEndDate, 
        watchedOnlineSeatsPerDay, // For online calcs
        liveAuditionCalculations, // For live calcs
        onlineAuditionCalculations, // For online calcs
        watchedScheduleForm // To get casting_location if needed
    ]);

    const handleUpdateScheduleClickInternal = (scheduleType) => {
        handleUpdateScheduleClick(scheduleType);

        const existingData = schedulesData[scheduleType.value];
        const defaultStartDate = minProjectDate || new Date();
        
        // When updating a schedule, set calDate to the existing schedule's start date
        // or a default. This is the initial state.
        setCalDate(existingData?.startDate && isValid(new Date(existingData.startDate)) ? new Date(existingData.startDate) : defaultStartDate);
    };

    // Effect to update calDate if the watchedStartDate or watchedEndDate changes
    // and calDate is outside the new range
    React.useEffect(() => {
        if (isSchedulingEdit && watchedStartDate && isValid(watchedStartDate) && watchedEndDate && isValid(watchedEndDate)) {
            // If calDate is not set or outside the new range, set it to the watchedStartDate
            if (!isValid(calDate) || !isWithinInterval(calDate, { start: watchedStartDate, end: watchedEndDate })) {
                setCalDate(watchedStartDate);
            }
        }
    }, [isSchedulingEdit, watchedStartDate, watchedEndDate, calDate, setCalDate]);


    return (
        <div className="animate-fade-in">
            {!isSchedulingEdit ? (        
                <>
                    <div className="form-section-title">Schedule Overview</div>
                    <Table striped bordered hover variant="dark" responsive className="mt-3">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Audition Type</th>
                                <th>Location</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Slots Per Day</th>
                                <th>Total Slots</th>
                                <th className="text-end">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {watchedAuditionType.map((opt, i) => {
                                const rowData = scheduleRows.find(r => r.typeValue === opt.value) || { 
                                    location: 'TBD', 
                                    startDate: 'N/A', 
                                    endDate: 'N/A', 
                                    slotsPerDay: 'N/A', 
                                    totalSlots: 'N/A' 
                                };
                                return (
                                    <tr key={opt.value}>
                                        <td>{i + 1}</td>
                                        <td><span className="badge bg-info text-dark">{opt.label}</span></td>
                                        <td>{rowData.location}</td>
                                        <td>{rowData.startDate}</td>
                                        <td>{rowData.endDate}</td>
                                        <td>{rowData.slotsPerDay}</td>
                                        <td>{rowData.totalSlots}</td>
                                        <td className="text-end">
                                            <Button variant="outline-light" size="sm" onClick={() => handleUpdateScheduleClickInternal(opt)}>
                                                <FaEdit /> Update Schedule
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </>
            ) : (
                <div className="role-schedule-container">
                    <Row>
                        <Col lg={isCurrentVideo ? 12 : 8}>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-4">
                                        <Form.Label className="small text-white fw-bold">Audition Type</Form.Label>
                                        <Select isMulti isDisabled value={singleAuditionOption} options={lookupOptions.audition_type} styles={customStyles} />
                                    </Form.Group>
                                </Col>
                                
                                {isCurrentVideo ? (
                                    <>
                                        <Col md={3}>
                                            <FormInput 
                                                name="online_seats_per_day_input" 
                                                label="Seats Per Day *" 
                                                type="number" 
                                                control={control} 
                                                errors={errors} 
                                                md={3}
                                                props={{ min: "0", placeholder: "Enter seats per day", className: "bg-dark text-white border-secondary" }}
                                                isRequired
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-4">
                                                <Form.Label className="small text-white fw-bold">Total Seats (Calculated)</Form.Label>
                                                <Form.Control value={onlineAuditionCalculations.total} readOnly className="bg-dark text-white border-secondary" />
                                            </Form.Group>
                                        </Col>
                                    </>
                                ) : (
                                    <Col md={3}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="small text-white fw-bold">Seats Available (Calculated)</Form.Label>
                                            <Form.Control value={liveAuditionCalculations.total} readOnly className="bg-dark text-white border-secondary" />
                                        </Form.Group>
                                    </Col>
                                )}
                                <Col md={3}>
                                    <Form.Group className="mb-4">
                                        <Form.Label className="small text-white fw-bold">Casting Location {getNestedError(errors, 'casting_location') && <span className="text-danger">*</span>}</Form.Label>
                                        <Controller 
                                            name="casting_location" 
                                            control={control} 
                                            rules={{ required: !isCurrentVideo ? 'Casting Location is required' : false }}
                                            render={({ field }) => (
                                                <Select 
                                                    {...field} 
                                                    options={locationOptions} 
                                                    isDisabled={isCurrentVideo} 
                                                    placeholder="Select Location..." 
                                                    styles={customStyles} 
                                                    isInvalid={!!getNestedError(errors, 'casting_location')}
                                                />
                                            )}
                                        />
                                        {getNestedError(errors, 'casting_location') && <Form.Control.Feedback type="invalid">{getNestedError(errors, 'casting_location')?.message}</Form.Control.Feedback>}
                                    </Form.Group>
                                </Col>

                                {!isCurrentVideo && (
                                    <Col md={3}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="small text-white fw-bold">Casting Interval</Form.Label>
                                            <Controller name="casting_time" control={control} render={({ field }) => <Select {...field} options={STATIC_OPTIONS.intervals} styles={customStyles} />} />
                                        </Form.Group>
                                    </Col>
                                )}
                            </Row>
                            <Row>
                                <Col md={3}>
                                    <FormDatePicker name="start_date" label="Start Date *" control={control} errors={errors} md={6} isRequired 
                                        minDate={minProjectDate} maxDate={maxProjectDate}
                                        rules={{ 
                                            required: 'Start Date is required',
                                            validate: value => {
                                                if (!value || !isValid(value)) return 'Invalid date';
                                                if (value < minProjectDate || value > maxProjectDate) return 'Date must be within project range';
                                                // Ensure start date is not after end date
                                                if (watchedEndDate && isValid(watchedEndDate) && value > watchedEndDate) return 'Start date cannot be after end date';
                                                return true;
                                            }
                                        }}
                                    />
                                </Col>
                                <Col md={3}>
                                    <FormDatePicker name="end_date" label="End Date *" control={control} errors={errors} md={6} isRequired 
                                        minDate={minProjectDate} maxDate={maxProjectDate}
                                        rules={{ 
                                            required: 'End Date is required',
                                            validate: value => {
                                                if (!value || !isValid(value)) return 'Invalid date';
                                                if (value < minProjectDate || value > maxProjectDate) return 'Date must be within project range';
                                                // Ensure end date is not before start date
                                                if (watchedStartDate && isValid(watchedStartDate) && value < watchedStartDate) return 'End date cannot be before start date';
                                                return true;
                                            }
                                        }}
                                    />
                                </Col>
                                {!isCurrentVideo && (
                                    <>
                                        <Col md={3}>
                                            <FormDatePicker name="start_time" label="Start Time" control={control} errors={errors} md={6} 
                                                showTimeSelect showTimeSelectOnly timeIntervals={15} dateFormat="h:mm aa" placeholderText="Select Time"
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <FormDatePicker name="end_time" label="End Time" control={control} errors={errors} md={6} 
                                                showTimeSelect showTimeSelectOnly timeIntervals={15} dateFormat="h:mm aa" placeholderText="Select Time"
                                            />
                                        </Col>
                                    </>
                                )}
                            </Row>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-4">
                                        <Form.Label className="small text-white fw-bold">Week Days Off</Form.Label>
                                        <Controller name="week_days_off" control={control} render={({ field }) => <Select isMulti {...field} options={STATIC_OPTIONS.week_days} styles={customStyles} />} />
                                    </Form.Group>
                                </Col>
                            </Row>
                            {!isCurrentVideo && <div className="text-start"><Button variant="primary" size="sm" className="mb-4" onClick={() => setShowCalendar(c => !c)}><FaCalendarAlt /> {showCalendar ? 'Hide' : 'View'} Slot Calendar</Button></div>}
                        </Col>
                        {!isCurrentVideo && showCalendar && (
                            <Col lg={4} className="border-start border-secondary ps-4">
                                <ScheduleCalendar 
                                    calDate={calDate} setCalDate={setCalDate} 
                                    // Pass specific dates and times, not the whole array
                                    scheduleStartDate={watchedStartDate} 
                                    scheduleEndDate={watchedEndDate}
                                    scheduleStartTime={watchedStartTime} // NEW PROP
                                    scheduleEndTime={watchedEndTime}     // NEW PROP
                                    castingInterval={watchedCastingTime} // NEW PROP
                                    watchedWeekDaysOff={watchedWeekDaysOff} 
                                    dateSpecificOffSlots={dateSpecificOffSlots}
                                    toggleSlotAvailability={toggleSlotAvailability}
                                    isCurrentVideo={isCurrentVideo}
                                />
                            </Col>
                        )}
                    </Row>
                    <div className="text-center mt-5">
                        <Button variant="outline-danger" className="px-5 py-2 fw-bold" onClick={handleDoneEditing}>
                            Done Editing
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StepSchedule;